import React, { useCallback, useEffect, useState } from 'react';
import profileApi from '../services/profileApi';
import { ActivityEntry } from './AdminPanel';
import Icon from './Icon';
import './ContactHistory.css';

/**
 * Who changed a person, and when: each change with its fields before and after, newest first,
 * shown like the admin Activity log. `refreshKey` changes after an edit, to show it.
 */
const ContactHistory = ({ contactId, refreshKey = 0 }) => {
  const [events, setEvents] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (beforeId) => {
    const page = await profileApi.getChangeHistory(contactId, beforeId);
    setNextBefore(page.next_before_id || null);
    return page.events || [];
  }, [contactId]);

  useEffect(() => {
    if (!contactId) return undefined;
    let cancelled = false;
    setError(null);
    load()
      .then(first => {
        if (!cancelled) setEvents(first);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId, refreshKey, load]);

  const showMore = async () => {
    setLoadingMore(true);
    try {
      const more = await load(nextBefore);
      setEvents(current => [...(current || []), ...more]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) return <p className="contact-history__error" role="alert"><Icon name="alert-triangle" /> {error}</p>;
  if (events === null) return <p className="contact-dialog-placeholder-message"><Icon name="loader" spin /> Loading…</p>;
  if (events.length === 0) {
    return <div className="contact-dialog-placeholder-message">No changes recorded yet.</div>;
  }

  return (
    <div className="contact-history">
      <ul className="adm-events contact-history__list">
        {events.map(event => <ActivityEntry key={event.id} event={event} />)}
      </ul>
      {nextBefore && (
        <button type="button" className="contact-history__more" onClick={showMore} disabled={loadingMore}>
          {loadingMore ? 'Loading…' : 'Show more'}
        </button>
      )}
    </div>
  );
};

export default ContactHistory;
